import { useEffect, useMemo, useState } from "react";
import { api } from "../data/api";
import { useMastersStore } from "../data/mastersStore";
import type { User, Warehouse } from "../data/schemaTypes";
import { Badge, Button, Card, Input } from "../components/ui";
import { useAuth } from "../auth/AuthContext";

/// SupervisorsPage — admin/coordinator gestiona qué almacenes ve cada
/// supervisor. Sin esto, el scope de un supervisor recién creado queda
/// vacío y no ve rutas (su listado de "Mis vehículos" en mobile / la
/// página de Avance en web están en blanco).
///
/// Layout: lista de supervisores a la izquierda, drawer con sus
/// almacenes asignados a la derecha. Click en un supervisor abre/cierra
/// el drawer. Botones "Agregar almacén" y "Quitar" (chip con X).
export function SupervisorsPage() {
  const { currentUser } = useAuth();
  // Solo admin/coordinator pueden entrar — el ProtectedRoute ya filtra,
  // pero defensivo: si por algún reason aterriza alguien sin permiso,
  // mostramos un mensaje claro.
  if (currentUser?.role !== "admin" && currentUser?.role !== "coordinator") {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <Card padding="lg" className="max-w-md text-center">
          <h2 className="text-base font-semibold text-ink mb-2">
            Acceso restringido
          </h2>
          <p className="text-sm text-sub">
            Solo administradores y coordinadores pueden asignar almacenes
            a supervisores.
          </p>
        </Card>
      </div>
    );
  }

  const users = useMastersStore((s) => s.users);
  const supervisors = useMemo(
    () => users.filter((u) => u.role === "supervisor"),
    [users],
  );
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return supervisors;
    return supervisors.filter(
      (s) =>
        s.full_name.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q),
    );
  }, [supervisors, search]);

  // Si el usuario seleccionado deja de existir (búsqueda lo filtra), no
  // dejamos un drawer huérfano.
  useEffect(() => {
    if (selectedId && !filtered.some((s) => s.id === selectedId)) {
      setSelectedId(null);
    }
  }, [filtered, selectedId]);

  return (
    <div className="flex-1 flex min-h-0">
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="px-4 pt-4 pb-2">
          <h1 className="text-xl font-semibold text-ink">Supervisores</h1>
          <p className="text-xs text-sub mt-0.5">
            {filtered.length} de {supervisors.length} supervisores. Cliqueá uno
            para gestionar sus almacenes asignados.
          </p>
        </header>

        <div className="px-4 pb-3">
          <Input
            placeholder="Buscar por nombre o email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex-1 overflow-auto px-4 pb-4">
          {filtered.length === 0 ? (
            <Card padding="lg" className="text-center">
              <p className="text-sm text-sub">
                {supervisors.length === 0
                  ? "No hay supervisores cargados. Creá uno desde Usuarios."
                  : "Sin resultados para esa búsqueda."}
              </p>
            </Card>
          ) : (
            <div className="space-y-2">
              {filtered.map((s) => (
                <SupervisorRow
                  key={s.id}
                  supervisor={s}
                  selected={selectedId === s.id}
                  onSelect={() => setSelectedId(s.id === selectedId ? null : s.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedId && (
        <SupervisorWarehousesDrawer
          key={selectedId}
          supervisor={supervisors.find((s) => s.id === selectedId)!}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

function SupervisorRow({
  supervisor,
  selected,
  onSelect,
}: {
  supervisor: User;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <Card
      padding="sm"
      onClick={onSelect}
      className={`cursor-pointer transition-colors ${
        selected ? "bg-primary/5 border-primary/30" : "hover:bg-page/60"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
            {supervisor.full_name.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink truncate">
              {supervisor.full_name}
            </div>
            <div className="text-xs text-sub truncate">{supervisor.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!supervisor.active && <Badge variant="failed">Inactivo</Badge>}
          <span className="text-sub text-lg">{selected ? "›" : "›"}</span>
        </div>
      </div>
    </Card>
  );
}

function SupervisorWarehousesDrawer({
  supervisor,
  onClose,
}: {
  supervisor: User;
  onClose: () => void;
}) {
  const allWarehouses = useMastersStore((s) => s.warehouses);

  const [assigned, setAssigned] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);

  // Carga inicial + recarga cuando cambia el supervisor seleccionado
  // (la key del componente lo fuerza, pero el effect protege contra
  // remounts implícitos del padre).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listSupervisorWarehouses(supervisor.id)
      .then((whs) => {
        if (cancelled) return;
        setAssigned(whs);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError((e as Error).message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [supervisor.id]);

  const assignedIds = useMemo(
    () => new Set(assigned.map((w) => w.id)),
    [assigned],
  );
  const availableWarehouses = useMemo(
    () => allWarehouses.filter((w) => !assignedIds.has(w.id) && w.active),
    [allWarehouses, assignedIds],
  );

  async function handleAssign(wh: Warehouse) {
    setSubmittingId(wh.id);
    setError(null);
    try {
      await api.assignSupervisorWarehouse(supervisor.id, wh.id);
      setAssigned((prev) => [...prev, wh].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) {
      setError(`No se pudo asignar: ${(e as Error).message}`);
    } finally {
      setSubmittingId(null);
    }
  }

  async function handleUnassign(wh: Warehouse) {
    setSubmittingId(wh.id);
    setError(null);
    try {
      await api.unassignSupervisorWarehouse(supervisor.id, wh.id);
      setAssigned((prev) => prev.filter((w) => w.id !== wh.id));
    } catch (e) {
      setError(`No se pudo quitar: ${(e as Error).message}`);
    } finally {
      setSubmittingId(null);
    }
  }

  return (
    <aside className="w-[420px] flex-shrink-0 border-l border-border bg-card flex flex-col">
      <header className="px-4 py-3 border-b border-border flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-sub">
            Almacenes del supervisor
          </div>
          <div className="text-sm font-semibold text-ink truncate mt-0.5">
            {supervisor.full_name}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          ✕
        </Button>
      </header>

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {error && (
          <div className="px-3 py-2 bg-state-failed/10 border border-state-failed/30 text-xs text-state-failed rounded">
            {error}
          </div>
        )}

        <section>
          <h3 className="text-[11px] uppercase tracking-wide text-sub mb-2">
            Asignados ({assigned.length})
          </h3>
          {loading ? (
            <div className="text-xs text-sub py-2">Cargando…</div>
          ) : assigned.length === 0 ? (
            <div className="text-xs text-sub italic py-2">
              Este supervisor no tiene almacenes asignados. Las pantallas de
              Avance, Flota y Mapa van a verse vacías hasta que asigne al
              menos uno.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {assigned.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => handleUnassign(w)}
                  disabled={submittingId === w.id}
                  title="Quitar"
                  className={`group flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors ${
                    submittingId === w.id
                      ? "border-border bg-page text-sub cursor-wait"
                      : "border-primary/30 bg-primary/10 text-primary hover:bg-state-failed/10 hover:border-state-failed/40 hover:text-state-failed"
                  }`}
                >
                  <span className="font-medium">{w.name}</span>
                  {w.code && (
                    <span className="font-mono text-[10px] opacity-70">
                      {w.code}
                    </span>
                  )}
                  <span className="text-sm leading-none">×</span>
                </button>
              ))}
            </div>
          )}
        </section>

        <section>
          <h3 className="text-[11px] uppercase tracking-wide text-sub mb-2">
            Disponibles para asignar ({availableWarehouses.length})
          </h3>
          {availableWarehouses.length === 0 ? (
            <div className="text-xs text-sub italic py-2">
              {allWarehouses.length === 0
                ? "Todavía no hay almacenes cargados en el sistema."
                : "Ya están todos asignados a este supervisor."}
            </div>
          ) : (
            <div className="space-y-1.5">
              {availableWarehouses.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => handleAssign(w)}
                  disabled={submittingId === w.id}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-border hover:border-primary/40 hover:bg-primary/5 transition-colors text-left disabled:opacity-50 disabled:cursor-wait"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-ink truncate">{w.name}</div>
                    {w.code && (
                      <div className="font-mono text-[10px] text-sub">
                        {w.code}
                      </div>
                    )}
                  </div>
                  <span className="text-primary text-sm font-semibold shrink-0">
                    + Asignar
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </aside>
  );
}
